<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Manage Settlements"
    class="w-full max-w-xl"
    data-test-id="manage-settlements-modal"
  >
    <!-- Loading: the vendor's current hold flag is still in flight -->
    <div
      v-if="showLoadingState"
      class="flex flex-col gap-4 pt-2"
      data-test-id="manage-settlements-loading-state"
    >
      <Skeleton width="14rem" height="1.5rem" />
      <Skeleton width="100%" height="2.5rem" />
    </div>

    <!-- No usable flag, so neither tab is safe to offer: holding an already-held
         supplier is a no-op, releasing one that was never held is not. -->
    <div
      v-else-if="showErrorState"
      class="flex flex-col items-start gap-3 pt-2"
      data-test-id="manage-settlements-error-state"
    >
      <p class="text-sm text-surface-700 dark:text-surface-300">
        Couldn't load the current settlement status for this supplier.
      </p>
      <Button
        label="Retry"
        icon="pi pi-refresh"
        size="small"
        severity="secondary"
        @click="onRetryFetch"
        data-test-id="manage-settlements-retry-btn"
      />
    </div>

    <Tabs
      v-else
      v-model:value="activeAction"
      class="p-0"
      data-test-id="manage-settlements-tabs"
    >
      <TabList>
        <Tab
          value="hold"
          :disabled="!isHoldEnabled"
          data-test-id="manage-settlements-tab-hold"
        >
          Hold Settlement
        </Tab>
        <Tab
          value="release"
          :disabled="!isReleaseEnabled"
          data-test-id="manage-settlements-tab-release"
        >
          Release Settlement
        </Tab>
      </TabList>

      <TabPanels class="px-0">
        <TabPanel value="hold" data-test-id="manage-settlements-panel-hold">
          <p
            class="text-base text-surface-900 dark:text-surface-0"
            data-test-id="manage-settlements-hold-question"
          >
            Are you sure you want to hold settlements for this supplier?
          </p>
          <p class="mt-2 text-sm text-surface-600 dark:text-surface-300">
            Payouts to the supplier's bank account will be paused until
            released.
          </p>
        </TabPanel>

        <TabPanel
          value="release"
          data-test-id="manage-settlements-panel-release"
        >
          <p
            class="text-base text-surface-900 dark:text-surface-0"
            data-test-id="manage-settlements-release-question"
          >
            Are you sure you want to release settlements for this supplier?
          </p>
          <p class="mt-2 text-sm text-surface-600 dark:text-surface-300">
            Payouts to the supplier's bank account will resume.
          </p>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <template #footer>
      <div class="flex gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          variant="outlined"
          size="small"
          :disabled="submitting"
          @click="onCancel"
          data-test-id="manage-settlements-cancel-btn"
        />
        <Button
          label="Confirm"
          size="small"
          :loading="submitting"
          :disabled="submitting || !canSubmit"
          @click="onSubmit"
          data-test-id="manage-settlements-confirm-btn"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useFrontegg } from '@frontegg/vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import { useFCCMerchantStore } from '@/stores/fcc'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import { resolveVendorName } from '@/util/fcc/vendorNameOverride'

const merchantStore = useFCCMerchantStore()
const toast = useToast()
const { authState } = useFrontegg()

const isVisible = ref(false)
const submitting = ref(false)
const activeAction = ref<'hold' | 'release'>('hold')

// MERCHANT_ID is an FCC key rather than a NetSuite record id, so it cannot be
// used here: NetSuite answers "The record instance does not exist" for it.
const vendorName = computed(() =>
  resolveVendorName(merchantStore.currentMerchant),
)

const isHeld = computed(() => merchantStore.holdPayment)
const showLoadingState = computed(() => merchantStore.holdPaymentLoading)
const showErrorState = computed(
  () => !showLoadingState.value && isHeld.value === null,
)

// Only the transition that changes something is offered.
const isHoldEnabled = computed(() => isHeld.value === false)
const isReleaseEnabled = computed(() => isHeld.value === true)
const canSubmit = computed(() =>
  activeAction.value === 'hold' ? isHoldEnabled.value : isReleaseEnabled.value,
)

watch(
  () => merchantStore.isManageSettlementsModalOpen,
  open => {
    isVisible.value = open
  },
)

watch(isVisible, open => {
  merchantStore.setIsManageSettlementsModalOpen(open)
  if (!open) {
    submitting.value = false
    return
  }
  // Land on whichever action is available, not a disabled tab.
  selectInitialTab()
})

// The fetch starts when the merchant loads, so it may still be in flight on
// open. Re-select once it settles.
watch(
  () => merchantStore.holdPaymentLoading,
  () => {
    if (isVisible.value) selectInitialTab()
  },
)

function selectInitialTab() {
  activeAction.value = isHeld.value === true ? 'release' : 'hold'
}

async function onRetryFetch() {
  if (!vendorName.value) return
  try {
    await merchantStore.fetchVendorHoldPayment(authState, vendorName.value)
    selectInitialTab()
  } catch (error) {
    console.error('Failed to reload settlement status:', error)
  }
}

function onCancel() {
  isVisible.value = false
}

async function onSubmit() {
  if (!vendorName.value || !canSubmit.value) return

  const wasAlreadyProcessing = submitting.value
  submitting.value = true
  if (wasAlreadyProcessing) return

  const shouldHold = activeAction.value === 'hold'

  try {
    await merchantStore.updateVendorHoldPayment(
      authState,
      vendorName.value,
      shouldHold,
    )
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: shouldHold
        ? 'Settlements held for this supplier'
        : 'Settlements released for this supplier',
      life: 3000,
    })
    isVisible.value = false

    // Re-read so the tabs reflect the server, not the value we assumed.
    void merchantStore
      .fetchVendorHoldPayment(authState, vendorName.value)
      .catch(refreshErr => {
        console.error(
          'Failed to refresh settlement status after submit:',
          refreshErr,
        )
      })
  } catch (error) {
    console.error('Failed to update settlement hold:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped></style>
